import { Box, Card, CardContent, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ChangelogDay, ChangelogEntry } from "@xop/dashboard-shared";
import { XIcon } from "lucide-react";

import { EmptyState } from "../../../components/ui/empty-state.js";
import { TYPE_META, repoLabel } from "./meta.js";

/**
 * The primary "event view" for the Change Log — a chronological,
 * day-grouped list of what shipped (newest day first). It is the left /
 * larger half of the event-first layout; the calendar on the right is the
 * date navigator/filter that drives `focusedDay`.
 *
 * Pure presentation: it renders whatever (already filtered + grouped)
 * `days` it is handed, so the type/repo/day filtering all lives in the
 * panel and stays testable in one place.
 */

/** Full, human date for a `YYYY-MM-DD` (local, no TZ shift). */
function formatDayLong(date: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export interface ChangelogEventListProps {
  /** Filtered + grouped days, newest first. */
  days: ChangelogDay[];
  /** The calendar-selected day, if the list is narrowed to one. */
  focusedDay: string | null;
  onClearFocus: () => void;
  loading: boolean;
}

export function ChangelogEventList({
  days,
  focusedDay,
  onClearFocus,
  loading,
}: ChangelogEventListProps): JSX.Element {
  if (loading && days.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: "text.disabled" }}>Loading…</Typography>
    );
  }

  if (days.length === 0) {
    return (
      <Box sx={{ display: "grid", gap: 1.5 }}>
        {focusedDay && <FocusPill date={focusedDay} onClear={onClearFocus} />}
        <EmptyState
          title="No matching updates"
          description="Nothing matches the current filters. Clear a filter, pick another day, or use ‹ › to browse other months."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      {focusedDay && <FocusPill date={focusedDay} onClear={onClearFocus} />}
      {days.map((day) => (
        <Box component="section" key={day.date} sx={{ display: "grid", gap: 1 }}>
          <Typography
            component="h3"
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              fontSize: 14,
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {formatDayLong(day.date)}
            <Box component="span" sx={{ fontWeight: 400, color: "text.disabled" }}>
              {day.count} update{day.count === 1 ? "" : "s"}
            </Box>
          </Typography>
          <Box sx={{ display: "grid", gap: 1 }}>
            {day.entries.map((e, i) => (
              <EntryCard key={`${e.repo}-${e.commit ?? i}`} entry={e} />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function FocusPill({
  date,
  onClear,
}: {
  date: string;
  onClear: () => void;
}): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClear}
      sx={{
        display: "inline-flex",
        height: "fit-content",
        width: "fit-content",
        maxWidth: "100%",
        alignItems: "center",
        gap: 0.75,
        alignSelf: "flex-start",
        whiteSpace: "nowrap",
        borderRadius: 1,
        border: 1,
        borderColor: "primary.main",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        px: 1.25,
        py: 0.5,
        fontSize: 12,
        fontWeight: 500,
        color: "primary.main",
        cursor: "pointer",
        font: "inherit",
        "&:focus-visible": {
          outline: (t) => `2px solid ${alpha(t.palette.primary.main, 0.6)}`,
          outlineOffset: 2,
        },
      }}
    >
      Showing {formatDayLong(date)}
      <XIcon size={14} aria-hidden="true" style={{ flexShrink: 0 }} />
      <Box component="span" sx={{ position: "absolute", width: 1, height: 1, p: 0, m: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}>
        — clear day filter
      </Box>
    </Box>
  );
}

function EntryCard({ entry }: { entry: ChangelogEntry }): JSX.Element {
  const meta = TYPE_META[entry.type];
  return (
    <Card>
      <CardContent sx={{ display: "grid", gap: 0.75, py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              borderRadius: 999,
              border: 1,
              borderColor: "divider",
              px: 0.75,
              py: 0.125,
              fontSize: 12,
              fontWeight: 500,
              color: "text.secondary",
            }}
          >
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: meta.dot }} />
            {meta.label}
          </Box>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              bgcolor: "surfaceSoft",
              px: 0.75,
              py: 0.125,
              fontSize: 12,
              fontWeight: 500,
              color: "text.secondary",
            }}
          >
            {repoLabel(entry.repo)}
          </Box>
          {entry.commit && (
            <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>
              {entry.commit}
            </Box>
          )}
          {entry.pr && (
            <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>
              {entry.pr}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
          {entry.title}
        </Typography>
      </CardContent>
    </Card>
  );
}
